// src/components/products/ProductBenefitForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchProduct,
  addBenefit,
  updateBenefit,
  clearProductError,
  clearProductSuccess,
} from '../../store/actions/productActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const ProductBenefitForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, benefitId } = useParams();
  const { selectedProduct: product, benefits, loading, error, success } = useSelector(
    (state) => state.products
  );

  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    description: '',
    order: 0,
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!benefitId;

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    productInfo: {
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    productName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '120px',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    amountInput: {
      position: 'relative',
    },
    amountPrefix: {
      position: 'absolute',
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    amountField: {
      paddingLeft: '3rem',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProduct(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (isEditMode && benefits) {
      const benefit = benefits.find(b => b.id === benefitId);
      if (benefit) {
        setFormData({
          title: benefit.title || '',
          amount: benefit.amount || '',
          description: benefit.description || '',
          order: benefit.order || 0,
        });
      }
    }
  }, [isEditMode, benefits, benefitId]);

  // Get next order number for new benefits
  useEffect(() => {
    if (!isEditMode && benefits) {
      const maxOrder = benefits?.reduce((max, b) => Math.max(max, b.order || 0), 0);
      setFormData(prev => ({ ...prev, order: maxOrder + 1 }));
    }
  }, [isEditMode, benefits]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearProductError());
      dispatch(clearProductSuccess());
    };
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (formData.order < 0) {
      newErrors.order = "L'ordre ne peut pas être négatif";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) || 0 : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateBenefit(id, benefitId, formData));
      } else {
        await dispatch(addBenefit(id, formData));
      }
      navigate(`/admin/products/${id}/benefits`);
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (formData.title || formData.amount || formData.description) {
      setShowCancelConfirm(true);
    } else {
      navigate(`/admin/products/${id}/benefits`);
    }
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate(`/admin/products/${id}/benefits`)}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>
            {isEditMode ? "Modifier l'avantage" : 'Nouvel avantage'}
          </h1>
        </div>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {product && (
        <div style={styles.productInfo}>
          <div style={styles.productName}>
            Produit: {product.name}
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearProductError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearProductSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="title">
            Titre *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            style={{
              ...styles.input,
              ...(errors.title ? styles.inputError : {}),
            }}
            placeholder="Ex: Assistance 24h/24"
          />
          {errors.title && (
            <div style={styles.errorText}>{errors.title}</div>
          )}
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="amount">
            Montant / Valeur
          </label>
          <div style={styles.amountInput}>
            <input
              type="text"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...styles.amountField,
              }}
              placeholder="Ex: Jusqu'à 5M FCFA"
            />
          </div>
          <div style={styles.helperText}>
            Indiquez le montant ou la valeur de cet avantage (ex: "Jusqu'à 5M FCFA", "100% pris en charge", etc.)
          </div>
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            style={styles.textarea}
            placeholder="Description détaillée de l'avantage..."
          />
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="order">
            Ordre d'affichage
          </label>
          <input
            type="number"
            id="order"
            name="order"
            value={formData.order}
            onChange={handleChange}
            min="0"
            style={{
              ...styles.input,
              ...(errors.order ? styles.inputError : {}),
            }}
          />
          {errors.order && (
            <div style={styles.errorText}>{errors.order}</div>
          )}
          <div style={styles.helperText}>
            Les avantages sont triés par ordre croissant
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Ajout...') 
              : (isEditMode ? 'Mettre à jour' : 'Ajouter')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate(`/admin/products/${id}/benefits`)}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer"
        type="warning"
      />
    </div>
  );
};

export default ProductBenefitForm;